import { FrontendEnvironmentSettings } from '@act/shared/environment';
import { DefaultPrivacyLevel, datadogRum } from '@datadog/browser-rum';

let userMonitoringInitialized = false;
let orientationListenerInitialized = false;
const safeActionPrefix = 'dd-safe-action-';
const obfuscatedActionTarget = 'TARGET OBFUSCATED';
const redactedQueryValue = ':redacted';
const sensitiveQueryParameterNames = new Set([
  'consentkey',
  'key',
  'memberid',
  'termkey',
]);

interface DatadogUrlEvent {
  resource?: {
    url?: string;
  };
  view?: {
    url?: string;
  };
}

/**
 * Initializes Datadog RUM once when monitoring is enabled.
 * @param environment - Runtime Care Portal environment settings.
 * @returns Nothing.
 */
export const initializeUserMonitoring = (
  environment: FrontendEnvironmentSettings
) => {
  if (userMonitoringInitialized || !environment.datadogRUMConfig.enabled) {
    return;
  }

  datadogRum.init({
    applicationId: environment.datadogRUMConfig.applicationId,
    clientToken: environment.datadogRUMConfig.clientToken,
    site: environment.datadogRUMConfig.site,
    service: environment.datadogRUMConfig.serviceName,
    env: environment.environment,
    // Specify a version number to identify the deployed version of your application in Datadog
    // version: '1.0.0',
    sessionSampleRate: 100,
    sessionReplaySampleRate: 100,
    trackUserInteractions: true,
    /*
     * Replay masks everything unless a surface opts in with datadogAllowProps.
     * A readable default was tried first and leaked member names wherever a
     * new component forgot a mask, so the burden sits on revealing rather
     * than on hiding.
     */
    defaultPrivacyLevel: 'mask',
    trackResources: true,
    trackLongTasks: true,
    beforeSend: event => {
      if (
        event.action &&
        event.action['target'] &&
        event.action['target']['name']
      ) {
        const originalName = event.action['target']['name'];
        if (typeof originalName === 'string') {
          event.action['target']['name'] =
            sanitizeDatadogActionTargetName(originalName);
        }
      }
      sanitizeDatadogEventUrls(event);
      return true;
    },
  });

  const getOrientation = () => {
    return window.innerWidth > window.innerHeight ? 'landscape' : 'portrait';
  };

  const setOrientationContext = () => {
    datadogRum.setGlobalContextProperty('ui.orientation', getOrientation());
    datadogRum.setGlobalContextProperty('ui.viewport.width', window.innerWidth);
    datadogRum.setGlobalContextProperty(
      'ui.viewport.height',
      window.innerHeight
    );
  };

  //set initial orientation
  setOrientationContext();

  const isIPad = () => {
    const isMac = navigator.userAgent.includes('Macintosh');
    const hasTouch =
      'maxTouchPoints' in navigator && navigator.maxTouchPoints > 1;
    return navigator.userAgent.includes('iPad') || (isMac && hasTouch);
  };

  datadogRum.setGlobalContextProperty('device.is_iPad', isIPad());

  // listen for updates
  if (!orientationListenerInitialized) {
    window.addEventListener('resize', setOrientationContext);
    window.addEventListener('orientationchange', setOrientationContext);
    orientationListenerInitialized = true;
  }
  userMonitoringInitialized = true;
};

/**
 * Options applied to generated Datadog tracking attributes.
 */
export interface DatadogTrackingOptions {
  privacyLevel?: DefaultPrivacyLevel;
}

/**
 * Datadog data attributes supported by Care Portal elements.
 */
export type DatadogTrackingProps = Record<string, string>;

/**
 * Returns a readable safe action name or an obfuscated fallback.
 * @param originalName - Action target name inferred or provided to Datadog.
 * @returns Sanitized action target name.
 */
export const sanitizeDatadogActionTargetName = (
  originalName: string
): string =>
  originalName.startsWith(safeActionPrefix)
    ? originalName.slice(safeActionPrefix.length)
    : obfuscatedActionTarget;

/**
 * Removes member identifiers and consent secrets from a captured URL.
 * @param rawUrl - Absolute or relative URL captured by Datadog.
 * @returns URL with member-identifying segments and query values redacted.
 */
export const sanitizeDatadogUrl = (rawUrl: string): string => {
  const isAbsoluteUrl = /^[a-z][a-z\d+\-.]*:/i.test(rawUrl);
  const placeholderOrigin = 'https://datadog-redaction.invalid';

  try {
    const parsedUrl = new URL(rawUrl, placeholderOrigin);
    parsedUrl.pathname = parsedUrl.pathname.replace(
      /(\/member\/)[^/?#]+/gi,
      '$1:member-id'
    );

    if (
      parsedUrl.hostname.toLowerCase().startsWith('consent.') &&
      /^\/[^/]+\/?$/.test(parsedUrl.pathname)
    ) {
      parsedUrl.pathname = '/:term-key';
    }

    for (const queryParameterName of [...parsedUrl.searchParams.keys()]) {
      if (
        sensitiveQueryParameterNames.has(queryParameterName.toLowerCase())
      ) {
        parsedUrl.searchParams.set(queryParameterName, redactedQueryValue);
      }
    }

    if (isAbsoluteUrl) {
      return parsedUrl.toString();
    }

    return `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
  } catch {
    return rawUrl.replace(/(\/member\/)[^/?#]+/gi, '$1:member-id');
  }
};

const sanitizeDatadogEventUrls = (event: DatadogUrlEvent): void => {
  if (event.view?.url) {
    event.view.url = sanitizeDatadogUrl(event.view.url);
  }
  if (event.resource?.url) {
    event.resource.url = sanitizeDatadogUrl(event.resource.url);
  }
};

/**
 * Generates safe Datadog action and optional replay privacy attributes.
 * @param name - Stable action name that contains no runtime member data.
 * @param options - Optional replay privacy settings.
 * @returns Attributes to spread onto a rendered element.
 */
export const generateDatadogTrackingProps = (
  name?: string,
  options?: DatadogTrackingOptions
): DatadogTrackingProps => {
  const { privacyLevel } = options || {};
  const props: DatadogTrackingProps = {};
  if (name) {
    props['data-dd-action-name'] = `${safeActionPrefix}${name}`;
  }
  if (privacyLevel) {
    props['data-dd-privacy'] = privacyLevel;
  }
  return props;
};

/**
 * Spread onto the narrowest stable root that renders member-identifying or
 * clinical free text.
 *
 * Replay masks by default, so this is a guard for regions that must stay
 * masked even when an ancestor is later allowed.
 */
export const datadogPhiMaskProps = {
  'data-dd-privacy': 'mask',
} as const satisfies DatadogTrackingProps;

/**
 * Spread onto reviewed application chrome that carries no member data —
 * navigation, dialog titles and instructions, buttons, column headers,
 * empty states.
 *
 * Never place this above a region that renders values from a member record;
 * an allowed ancestor reveals its whole subtree.
 */
export const datadogAllowProps = {
  'data-dd-privacy': 'allow',
} as const satisfies DatadogTrackingProps;
